import type { LucideIcon } from 'lucide-react'
import { Activity, FileText, UserPlus, CalendarDays, Wallet } from 'lucide-react'
import { DateValue } from './DateValue'
import { cn } from '@/lib/utils'

export interface ActivityItem {
  id: string
  type: string
  message: string
  occurredAt: string
  actorName?: string | null
  link?: string | null
}

/** Icon per activity type, so the feed is scannable without reading every line. */
const ICONS: Record<string, { icon: LucideIcon; tint: string }> = {
  EMPLOYEE_ADDED: { icon: UserPlus, tint: 'bg-[var(--color-tint-green)] text-[var(--color-success)]' },
  LEAVE_REQUEST: { icon: CalendarDays, tint: 'bg-[var(--color-tint-orange)] text-[var(--color-warning)]' },
  PAYROLL: { icon: Wallet, tint: 'bg-[var(--color-tint-blue)] text-[var(--color-info)]' },
  DOCUMENT: { icon: FileText, tint: 'bg-[var(--color-primary-soft)] text-[var(--color-primary)]' },
}

export function ActivityFeed({ items, className }: { items: ActivityItem[]; className?: string }) {
  return (
    <ul className={cn('divide-y divide-[var(--color-border)]', className)}>
      {items.map((item) => {
        const config = ICONS[item.type] ?? {
          icon: Activity,
          tint: 'bg-[var(--color-page)] text-[var(--color-muted)]',
        }
        const Icon = config.icon
        return (
          <li key={item.id} className="flex items-start gap-3 py-2.5">
            <span className={cn('mt-0.5 grid size-7 shrink-0 place-items-center rounded-full', config.tint)}>
              <Icon className="size-3.5" aria-hidden="true" />
            </span>
            <p className="min-w-0 flex-1 text-sm text-[var(--color-heading)]">{item.message}</p>
            <DateValue
              value={item.occurredAt}
              relative
              className="shrink-0 whitespace-nowrap text-xs text-[var(--color-muted)]"
            />
          </li>
        )
      })}
    </ul>
  )
}
